import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  CREATABLE_ROLES,
  ROLE_LABELS,
  ROLE_BADGE_CLASSES,
  canCreateKeys as checkCanCreateKeys,
  canManageUsers as checkCanManageUsers,
} from "../roles";

export const API_BASE = "https://license-worker.alphatrendz99.workers.dev";

export const formatISTDateTime = (utcDateString) => {
  if (!utcDateString) return "—";
  const date = new Date(utcDateString);
  return date
    .toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: false,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    .replace(",", "");
};

export const getISTDateKey = (utcDateString) => {
  if (!utcDateString) return "";
  const date = new Date(utcDateString);
  const formatter = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return formatter.format(date); // "DD/MM/YYYY"
};

export const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};

const DashboardContext = createContext(null);

export const DashboardProvider = ({ children }) => {
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  });

  const role = user.role || "seller";
  const canCreateKeys = checkCanCreateKeys(role);
  const canManageUsers = checkCanManageUsers(role);
  const creatableRoles = CREATABLE_ROLES[role] || [];

  const [rawLicenses, setRawLicenses] = useState([]);
  const [groupedLicenses, setGroupedLicenses] = useState({});
  const [loadingLicenses, setLoadingLicenses] = useState(false);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [copiedKey, setCopiedKey] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const authConfig = useCallback(
    () => ({
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    }),
    []
  );

  const notify = (type, text) => {
    setActionMessage({ type, text, id: Date.now() });
    setTimeout(() => {
      setActionMessage((curr) => (curr?.text === text ? null : curr));
    }, 4000);
  };

  const fetchLicenses = useCallback(async () => {
    setLoadingLicenses(true);
    try {
      const res = await axios.get(`${API_BASE}/api/licenses`, authConfig());
      const data = res.data || [];
      setRawLicenses(data);

      const grouped = {};
      data.forEach((item) => {
        const dateKey = getISTDateKey(item.genTime);
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(item);
      });
      setGroupedLicenses(grouped);
    } catch (err) {
      console.error("Error fetching licenses:", err);
    } finally {
      setLoadingLicenses(false);
    }
  }, [authConfig]);

  const fetchUsers = useCallback(async () => {
    if (!canManageUsers) return;
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API_BASE}/api/users`, authConfig());
      setUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoadingUsers(false);
    }
  }, [authConfig, canManageUsers]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchLicenses(),
      canManageUsers ? fetchUsers() : Promise.resolve(),
    ]);
  }, [fetchLicenses, fetchUsers, canManageUsers]);

  useEffect(() => {
    fetchLicenses();
    if (canManageUsers) {
      fetchUsers();
    }
  }, [fetchLicenses, fetchUsers, canManageUsers]);

  const handleCopyKey = (key) => {
    if (!key) return;
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  const handleCreateLicense = async ({ name, duration, payment }) => {
    const cleanedName = name.replace(/\s+/g, "").substring(0, 6).toLowerCase();
    const durationMap = {
      7: "seven",
      15: "fifteen",
      30: "thirty",
    };
    const durationWord = durationMap[duration] || "seven";
    const randomPart = Math.random().toString(36).substring(2, 7);
    const licenseKey = `${cleanedName}${durationWord}${randomPart}`;

    const now = new Date();
    const expiryDate = new Date(now.getTime() + duration * 24 * 60 * 60 * 1000);
    const genTime = now.toISOString();

    const payload = {
      name,
      licenseKey,
      expiryDate: expiryDate.toISOString(),
      plan: duration,
      genTime,
      hasUsed: false,
      payment,
    };

    try {
      await axios.post(`${API_BASE}/api/licenses`, payload, authConfig());
      notify("success", `License key '${licenseKey}' successfully generated for ${name}!`);
      await fetchLicenses();
      return { success: true, key: licenseKey };
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create license key.";
      notify("error", msg);
      throw new Error(msg);
    }
  };

  const handleDeleteLicense = async (id) => {
    const confirm = window.confirm("Are you sure you want to permanently delete this license key?");
    if (!confirm) return false;

    try {
      await axios.delete(`${API_BASE}/api/licenses/${id}`, authConfig());
      notify("success", "License key removed from database.");
      await fetchLicenses();
      return true;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to delete license.";
      notify("error", msg);
      return false;
    }
  };

  const handleHasUsedChange = async (id, value) => {
    try {
      await axios.put(`${API_BASE}/api/licenses/${id}`, { hasUsed: value }, authConfig());
      notify("success", `Status updated: ${value ? "true" : "false"}`);
      await fetchLicenses();
      return true;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update license status.";
      notify("error", msg);
      return false;
    }
  };

  const handleCreateUser = async ({ username, password, targetRole }) => {
    try {
      const res = await axios.post(
        `${API_BASE}/api/users`,
        { username, password, role: targetRole },
        authConfig()
      );
      notify("success", res.data?.message || `Operator @${username} provisioned successfully!`);
      await fetchUsers();
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to provision user.";
      notify("error", msg);
      throw new Error(msg);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  // Stats calculation
  const stats = useMemo(() => {
    const total = rawLicenses.length;
    const expired = rawLicenses.filter((l) => isExpired(l.expiryDate)).length;
    const active = total - expired;
    const used = rawLicenses.filter((l) => l.hasUsed).length;
    const unused = total - used;

    const plan7 = rawLicenses.filter((l) => Number(l.plan) === 7).length;
    const plan15 = rawLicenses.filter((l) => Number(l.plan) === 15).length;
    const plan30 = rawLicenses.filter((l) => Number(l.plan) === 30).length;

    return {
      totalLicenses: total,
      expiredCount: expired,
      activeCount: active,
      usedCount: used,
      unusedCount: unused,
      totalUsers: users.length,
      plan7,
      plan15,
      plan30,
    };
  }, [rawLicenses, users]);

  const value = {
    user,
    role,
    roleLabel: ROLE_LABELS[role] || role,
    roleBadgeClass: ROLE_BADGE_CLASSES[role] || "neutral",
    canCreateKeys,
    canManageUsers,
    creatableRoles,
    rawLicenses,
    groupedLicenses,
    loadingLicenses,
    users,
    loadingUsers,
    copiedKey,
    handleCopyKey,
    handleCreateLicense,
    handleDeleteLicense,
    handleHasUsedChange,
    handleCreateUser,
    handleLogout,
    fetchLicenses,
    fetchUsers,
    refreshAll,
    stats,
    actionMessage,
    formatISTDateTime,
    getISTDateKey,
    isExpired,
  };

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};
